import { useState, useEffect, useRef } from 'react'
import { Plus, Target, Bell, Menu, User, Camera, X } from 'lucide-react'
import { useAuthStore } from '@/stores/simpleAuthStore'
import { apiService } from '@/services/api'
import toast from 'react-hot-toast'
import Map, { MapMarker, MapRef } from '@/components/Map'

const SimpleMapPage = () => {
  const { user } = useAuthStore()
  const [searchQuery, setSearchQuery] = useState('')
  const [currentMode, setCurrentMode] = useState<'pings' | 'listings'>('pings')
  const [markers, setMarkers] = useState<MapMarker[]>([])
  const [userLocation, setUserLocation] = useState<[number, number]>([40.7128, -74.0060])
  const [isLoadingLocation, setIsLoadingLocation] = useState(false)
  const [showPingPopup, setShowPingPopup] = useState(false)
  const [pingLocation, setPingLocation] = useState<[number, number] | null>(null)
  const [capturedPhoto, setCapturedPhoto] = useState<string | null>(null)
  const [isPingModeActive, setIsPingModeActive] = useState(false)
  const [pingRadius] = useState(200) // 200 meters radius
  const [pingNote, setPingNote] = useState('')
  const mapRef = useRef<MapRef>(null)

  // Load markers based on current mode
  useEffect(() => {
    loadMarkers()
  }, [currentMode, userLocation])

  const loadMarkers = async () => {
    try {
      if (currentMode === 'pings') {
        const pings = await apiService.getPings(userLocation[0], userLocation[1], 5)
        const pingMarkers: MapMarker[] = pings.map(ping => ({
          id: ping.id,
          position: [ping.lat, ping.lng],
          title: 'Available Parking',
          description: ping.note || 'Parking spot available',
          type: 'ping',
          status: ping.status as any,
          timestamp: ping.created_at,
          photo: ping.photo_url,
          userId: ping.user_id
        }))
        setMarkers(pingMarkers)
      } else {
        const spaces = await apiService.getPrivateSpaces(userLocation[0], userLocation[1], 5)
        const spaceMarkers: MapMarker[] = spaces.map(space => ({
          id: space.id,
          position: [space.lat, space.lng],
          title: space.title,
          description: space.description,
          type: 'listing',
          status: space.is_active ? 'available' : 'expired',
          price: space.hourly_rate,
          userId: space.owner_id
        }))
        setMarkers(spaceMarkers)
      }
    } catch (error) {
      console.error('Failed to load markers:', error)
      // Use empty array on error
      setMarkers([])
    }
  }

  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && searchQuery.trim()) {
      toast.success(`🔍 Searching for: ${searchQuery}`)
      setSearchQuery('')
    }
  }

  const getCurrentLocation = (): Promise<[number, number]> => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported'))
        return
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve([position.coords.latitude, position.coords.longitude])
        },
        (error) => {
          reject(error)
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
      )
    })
  }

  const centerOnUser = async () => {
    setIsLoadingLocation(true)
    try {
      const location = await getCurrentLocation()
      setUserLocation(location)

      // Center the map on user location
      if (mapRef.current) {
        mapRef.current.centerMap(location[0], location[1], 15)
      }

      toast.success('📍 Centered on your location!')
    } catch (error) {
      console.error('Error getting location:', error)
      toast.error('Could not get your location. Using default location.')
    } finally {
      setIsLoadingLocation(false)
    }
  }

  const handleMarkerClick = (marker: MapMarker) => {
    if (marker.type === 'ping') {
      toast.success(`🅿️ Ping: ${marker.title}`)
    } else if (marker.type === 'listing') {
      toast.success(`🏠 Listing: ${marker.title} - $${marker.price}/hr`)
    }
  }

  // Calculate distance between two coordinates in meters
  const calculateDistance = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
    const R = 6371e3 // Earth's radius in meters
    const φ1 = lat1 * Math.PI/180
    const φ2 = lat2 * Math.PI/180
    const Δφ = (lat2-lat1) * Math.PI/180
    const Δλ = (lng2-lng1) * Math.PI/180

    const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ/2) * Math.sin(Δλ/2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a))

    return R * c
  }

  const handleMapClick = (lat: number, lng: number) => {
    // Only allow pinging if ping mode is active and in pings mode
    if (currentMode === 'pings' && isPingModeActive) {
      // Check if click is within allowed radius of user location
      const distance = calculateDistance(userLocation[0], userLocation[1], lat, lng)

      if (distance <= pingRadius) {
        setPingLocation([lat, lng])
        setShowPingPopup(true)
        setCapturedPhoto(null)
        setPingNote('')
      } else {
        toast.error(`📍 You can only ping within ${pingRadius}m of your location!`)
      }
    }
  }

  // Handle ping parking button click
  const handlePingParkingClick = async () => {
    if (!isPingModeActive) {
      // First get user location
      setIsLoadingLocation(true)
      try {
        const location = await getCurrentLocation()
        setUserLocation(location)

        // Center the map on user location
        if (mapRef.current) {
          mapRef.current.centerMap(location[0], location[1], 16)
        }

        // Activate ping mode
        setIsPingModeActive(true)
        toast.success('📍 Ping mode activated! Tap within the circle to ping a parking spot.')
      } catch (error) {
        console.error('Error getting location:', error)
        toast.error('Could not get your location. Please enable location services.')
      } finally {
        setIsLoadingLocation(false)
      }
    } else {
      // Deactivate ping mode
      setIsPingModeActive(false)
      toast.success('📍 Ping mode deactivated.')
    }
  }

  // Handle photo capture
  const handlePhotoCapture = async () => {
    try {
      // In a real app, this would use the camera API
      // For now, we'll simulate photo capture
      const canvas = document.createElement('canvas')
      canvas.width = 300
      canvas.height = 200
      const ctx = canvas.getContext('2d')
      if (ctx) {
        ctx.fillStyle = '#f0f0f0'
        ctx.fillRect(0, 0, 300, 200)
        ctx.fillStyle = '#666'
        ctx.font = '16px Arial'
        ctx.textAlign = 'center'
        ctx.fillText('📷 Photo captured!', 150, 100)
        ctx.fillText(new Date().toLocaleString(), 150, 130)
        setCapturedPhoto(canvas.toDataURL())
      }
      toast.success('📷 Photo captured!')
    } catch (error) {
      toast.error('Failed to capture photo')
    }
  }

  // Handle ping submission
  const handleSubmitPing = async () => {
    if (!pingLocation || !capturedPhoto) {
      toast.error('Please capture a photo first!')
      return
    }

    if (!user) {
      toast.error('Please log in to create a ping!')
      return
    }

    try {
      const pingData = {
        lat: pingLocation[0],
        lng: pingLocation[1],
        photo_url: capturedPhoto,
        note: pingNote || undefined
      }

      const newPing = await apiService.createPing(pingData)

      // Add the new ping to the markers
      const newMarker: MapMarker = {
        id: newPing.id,
        position: [newPing.lat, newPing.lng],
        title: 'Available Parking',
        description: newPing.note || 'Parking spot available',
        type: 'ping',
        status: 'active',
        timestamp: newPing.created_at,
        photo: newPing.photo_url,
        userId: newPing.user_id
      }

      setMarkers(prev => [...prev, newMarker])

      // Close popup and deactivate ping mode
      setShowPingPopup(false)
      setPingLocation(null)
      setCapturedPhoto(null)
      setPingNote('')
      setIsPingModeActive(false)

      toast.success('🅿️ Parking spot pinged successfully!')
    } catch (error) {
      console.error('Failed to create ping:', error)
      toast.error('Failed to create ping. Please try again.')
    }
  }

  return (
    <div className="h-screen relative bg-gray-50">
      {/* Interactive Map */}
      <Map
        ref={mapRef}
        center={userLocation}
        zoom={13}
        markers={markers}
        onMarkerClick={handleMarkerClick}
        onMapClick={handleMapClick}
        className="w-full h-full"
        userLocation={userLocation}
        isPingModeActive={isPingModeActive}
        pingRadius={pingRadius}
      />

      {/* Top Status Bar */}
      <div className="absolute top-0 left-0 right-0 z-50 bg-white px-4 py-2">
        <div className="flex items-center justify-between text-sm">
          <span className="font-medium">9:11</span>
          <div className="flex items-center gap-1">
            <span>📶</span>
            <span>📶</span>
            <span>🔋</span>
            <span>60%</span>
          </div>
        </div>
      </div>

      {/* Top UI Overlay */}
      <div className="absolute top-12 left-0 right-0 z-50 px-4 pt-4">
        {/* Header Row */}
        <div className="flex items-center justify-between mb-4">
          {/* Profile Avatar */}
          <div className="w-12 h-12 bg-gray-300 rounded-full flex items-center justify-center">
            {user?.avatar_url ? (
              <img src={user.avatar_url} alt="Profile" className="w-full h-full rounded-full object-cover" />
            ) : (
              <User className="w-6 h-6 text-gray-600" />
            )}
          </div>

          {/* Search Bar */}
          <div className="flex-1 mx-4 relative">
            <input
              type="text"
              placeholder="Search by location"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={handleSearchKeyPress}
              className="w-full pl-4 pr-12 py-3 bg-white rounded-full text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm border border-gray-200"
            />
            <button
              onClick={centerOnUser}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-blue-500 hover:text-blue-600 transition-colors"
            >
              <Target className="w-5 h-5" />
            </button>
          </div>

          {/* Menu Button */}
          <button className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm border border-gray-200">
            <Menu className="w-6 h-6 text-gray-600" />
          </button>

          {/* Notification Bell */}
          <button className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center shadow-lg ml-2">
            <Bell className="w-6 h-6 text-white" />
          </button>
        </div>

        {/* Mode Toggle */}
        <div className="flex bg-white rounded-full p-1 shadow-sm border border-gray-200 max-w-xs">
          <button
            onClick={() => setCurrentMode('pings')}
            className={`flex-1 py-2 px-4 rounded-full text-sm font-medium transition-colors ${
              currentMode === 'pings'
                ? 'bg-blue-500 text-white shadow-sm'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            Vehicle Parking
          </button>
          <button
            onClick={() => setCurrentMode('listings')}
            className={`flex-1 py-2 px-4 rounded-full text-sm font-medium transition-colors ${
              currentMode === 'listings'
                ? 'bg-blue-500 text-white shadow-sm'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            Parking Space
          </button>
        </div>
      </div>

      {/* Bottom Action Button */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-50">
        <button
          onClick={handlePingParkingClick}
          disabled={isLoadingLocation}
          className={`flex items-center justify-center gap-2 px-6 py-3 rounded-full shadow-xl transition-all duration-300 font-medium text-sm min-w-[140px] h-[50px] ${
            isPingModeActive
              ? 'bg-red-500 hover:bg-red-600 text-white'
              : 'bg-blue-500 hover:bg-blue-600 text-white'
          } ${isLoadingLocation ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {isLoadingLocation ? (
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              <Plus className="w-4 h-4" />
              <span>{isPingModeActive ? 'Cancel Ping' : 'Ping Parking'}</span>
            </>
          )}
        </button>
      </div>

      {/* Location Button - Bottom Right */}
      <div className="absolute bottom-8 right-4 z-50">
        <button
          onClick={centerOnUser}
          disabled={isLoadingLocation}
          className={`w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-colors ${
            isLoadingLocation
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-white hover:bg-gray-50 border border-gray-200'
          }`}
        >
          {isLoadingLocation ? (
            <div className="w-6 h-6 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
          ) : (
            <Target className="w-6 h-6 text-blue-500" />
          )}
        </button>
      </div>

      {/* Ping Parking Popup */}
      {showPingPopup && pingLocation && (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Ping Parking Spot</h3>
              <button
                onClick={() => setShowPingPopup(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                Location: {pingLocation[0].toFixed(4)}, {pingLocation[1].toFixed(4)}
              </p>
              <p className="text-sm text-gray-600 mb-4">
                Time: {new Date().toLocaleString()}
              </p>
            </div>

            {/* Photo Section */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Take a photo of the parking spot
              </label>

              {capturedPhoto ? (
                <div className="relative">
                  <img
                    src={capturedPhoto}
                    alt="Captured parking spot"
                    className="w-full h-32 object-cover rounded-lg border"
                  />
                  <button
                    onClick={() => setCapturedPhoto(null)}
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={handlePhotoCapture}
                  className="w-full h-32 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center text-gray-500 hover:border-blue-400 hover:text-blue-500 transition-colors"
                >
                  <Camera className="w-8 h-8 mb-2" />
                  <span className="text-sm">Tap to capture photo</span>
                </button>
              )}
            </div>

            {/* Note Section */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Add a note (optional)
              </label>
              <textarea
                value={pingNote}
                onChange={(e) => setPingNote(e.target.value)}
                placeholder="e.g., Blue Honda Civic leaving soon..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                rows={2}
              />
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => setShowPingPopup(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitPing}
                disabled={!capturedPhoto}
                className={`flex-1 px-4 py-2 rounded-lg transition-colors ${
                  capturedPhoto
                    ? 'bg-blue-500 text-white hover:bg-blue-600'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Ping Spot
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default SimpleMapPage
